import React, {forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import 'styles/chat/EmojiMenuButton.css';
import 'styles/chat/ChatScrollbar.css';
import itemTypeToEmoji from 'util/itemTypeToEmoji';
import ItemTypeToEmoji from 'util/itemTypeToEmoji';
import {MarketContext} from 'util/market/MarketContext';
import ChatEmojiButtonSvg from 'util/svg/ChatEmojiButtonSvg';
import AddReactionEmojiSvg from 'util/svg/AddReactionEmojiSvg';
import {useSessionConfiguration} from 'util/SessionConfigurationContext';
import SignInPopup from 'components/login/SignInPopup';
import {usePopup} from 'popup/PopupContext';
import {UserContext} from 'components/user/UserContext';

const UniversalEmojiMenu = forwardRef(({
                                           onKeyPress,
                                           onOpenedChange,
                                           isAddReaction = false,
                                           size = '26px',
                                           calmColor = 'gray',
                                           activeColor = 'yellow',
                                           ignoreClickRef
                                       }, ref) => {
    const [emojiListVisible, setEmojiListVisible] = useState(false);
    const [isEnabled, setIsEnabled] = useState(true);
    const [isSelected, setIsSelected] = useState(false);
    const emojiButtonRef = useRef(null);
    const outerDivRef = useRef(null);
    const [unownedEmojis, setUnownedEmojis] = useState([]);
    const [isHovered, setIsHovered] = useState(false);
    const {allowedEmojis} = useContext(MarketContext);
    const [specificAllowedEmojis, setSpecificAllowedEmojis] = useState([]);
    const [emojiColor, setEmojiColor] = useState(calmColor);
    const {isTouch} = useSessionConfiguration();
    const listRef = useRef(null);
    const [transformOffset, setTransformOffset] = useState(0);
    const {openPopup} = usePopup();
    const {user} = useContext(UserContext);

    useEffect(() => {
        if (emojiListVisible && listRef.current) {
            const rect = listRef.current.getBoundingClientRect();
            if (rect.right > window.innerWidth) {
                const overflow = rect.right - window.innerWidth;
                setTransformOffset(-overflow - 5); // move it left by the overflow plus some padding
            }
        } else {
            setTransformOffset(0);
        }
    }, [emojiListVisible]);

    useImperativeHandle(ref, () => ({
        setEnabled: () => setIsEnabled(true),
        setDisabled: () => {
            setIsEnabled(false);
            setEmojiListVisible(false);
            if (onOpenedChange) {
                onOpenedChange(false);
            }
        },
        hideEmojiPopup: () => {
            setEmojiListVisible(false);
            if (onOpenedChange) {
                onOpenedChange(false);
            }
            setIsSelected(false);
        }
    }));

    useEffect(() => {
        setEmojiColor(isHovered || isSelected ? activeColor : calmColor);
    }, [isHovered, isSelected, activeColor, calmColor]);

    const toggleEmojiList = () => {
        if (!user.isLoggedIn) {
            openPopup(<SignInPopup/>);
            return;
        }
        if (isEnabled) {
            if (onOpenedChange) {
                onOpenedChange(!emojiListVisible);
            }
            setEmojiListVisible(!emojiListVisible);
            setIsSelected(!isSelected);
        }
    };

    useEffect(() => {
        let myAllowedEmojis;
        if (isAddReaction) {
            const thumbUpEmoji = '👍';
            if (!allowedEmojis.includes(thumbUpEmoji)) {
                myAllowedEmojis = [...allowedEmojis, thumbUpEmoji];
            } else {
                myAllowedEmojis = allowedEmojis;
            }
        } else {
            myAllowedEmojis = allowedEmojis;
        }
        setSpecificAllowedEmojis(myAllowedEmojis);

        const allEmojis = Object.values(itemTypeToEmoji.getAllEmojis()).filter(value => typeof value === 'string');
        setUnownedEmojis(allEmojis.filter(emoji => !myAllowedEmojis.includes(emoji)));

    }, [allowedEmojis, isAddReaction]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (outerDivRef.current && outerDivRef.current.contains(event.target)) {
                return;
            }
            if (ignoreClickRef && ignoreClickRef.current && ignoreClickRef.current.contains(event.target)) {
                return;
            }
            setEmojiListVisible(false);
            if (onOpenedChange) {
                onOpenedChange(false);
            }
            setIsSelected(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [outerDivRef]);

    const getEmojiDetails = (emoji) => {
        const type = Object.keys(itemTypeToEmoji).find(key => itemTypeToEmoji[key].emoji === emoji);
        const rarity = itemTypeToEmoji.getRarity(type);
        return {rarity, type};
    };

    const mouseEnter = () => {
        if (!isTouch) {
            setIsHovered(true);
        }
    };

    const mouseLeave = () => {
        if (!isTouch) {
            setIsHovered(false);
        }
    };

    return (
        <div ref={outerDivRef}
             style={{display: 'inline-block', position: 'relative', maxWidth: size, maxHeight: size}}>
            <div
                className="emoji-button"
                onMouseEnter={mouseEnter}
                onMouseLeave={mouseLeave}
                style={{
                    width: size,
                    height: size,
                    backgroundColor: (!isAddReaction && (isHovered || isSelected) ? 'gray' : 'transparent')
                }}
                ref={emojiButtonRef}
                onClick={(event) => {
                    event.preventDefault();
                    toggleEmojiList();
                }}
            >
                {isAddReaction ?
                    <AddReactionEmojiSvg size={size} color={emojiColor}/>
                    : <ChatEmojiButtonSvg size={size} color={emojiColor}/>
                }
            </div>
            {emojiListVisible && (
                <div ref={listRef} className="emoji-list" style={{transform: `translateX(${transformOffset}px)`}}>
                    {specificAllowedEmojis.map((emoji, index) => {
                        const {rarity, type} = getEmojiDetails(emoji);
                        return (
                            <div
                                key={index}
                                className="emoji-item"
                                onClick={(e) => {
                                    e.preventDefault(); // Prevent focus loss
                                    onKeyPress(emoji);
                                }}
                                style={{backgroundColor: getBackgroundColor(rarity)}}
                                data-tooltip-id="main-tooltip"
                                data-tooltip-content={ItemTypeToEmoji[type].translation}
                            >
                                {emoji}
                            </div>
                        );
                    })}
                    {unownedEmojis.map((emoji, index) => {
                        const {rarity, type} = getEmojiDetails(emoji);
                        return (
                            <div
                                key={index}
                                className="emoji-item unowned-emoji"
                                style={{backgroundColor: getBackgroundColor(rarity)}}
                                data-tooltip-id="main-tooltip"
                                data-tooltip-content={'Unowned ' + ItemTypeToEmoji[type].translation}
                            >
                                {emoji}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

const getBackgroundColor = (rarity) => {
    switch (rarity) {
        case 'UNCOMMON':
            return 'rgb(216, 247, 255)';
        case 'ELUSIVE':
            return 'rgb(246, 255, 0)';
        case 'LETTER':
            return 'white';
        case 'EPIC':
            return 'rgb(194, 127, 255)';
        case 'RARE':
            return 'rgb(109, 187, 255)';
        default:
            return 'transparent';
    }
};

export default UniversalEmojiMenu;